import { useEffect, type HTMLAttributes } from 'react'
import { cn } from '@/lib/utils'
import quedaSvg from '@/assets/characters/queda.svg'
import ddamiSvg from '@/assets/characters/ddami.svg'
import chaeliSvg from '@/assets/characters/chaeli.svg'

type MascotName = 'queda' | 'ddami' | 'chaeli'
type Shape = 'circle' | 'roundbox'

export type MascotProps = Omit<HTMLAttributes<HTMLDivElement>, 'children'> & {
  name: MascotName
  shape?: Shape
  /** px 기본 80 — 32 미만은 console.warn */
  size?: number
  /** true 면 aria-hidden, role="presentation" — 순수 장식 용도. */
  decorative?: boolean
}

const nameSrc: Record<MascotName, string> = {
  queda: quedaSvg,
  ddami: ddamiSvg,
  chaeli: chaeliSvg,
}

const shapeClass: Record<Shape, string> = {
  circle: 'rounded-full',
  roundbox: 'rounded-lc-xl',
}

export function Mascot({
  name,
  shape = 'circle',
  size = 80,
  className,
  style,
  decorative = false,
  ...rest
}: MascotProps) {
  useEffect(() => {
    if (import.meta.env.DEV && size < 32) {
      // DESIGN.md §4.2: 32px 미만은 아이콘 대체 권장
      console.warn(
        `[Mascot] size ${size}px < 32px — DESIGN.md recommends icon instead of mascot at this size.`,
      )
    }
  }, [size])

  return (
    <div
      {...rest}
      {...(decorative
        ? { 'aria-hidden': true, role: 'presentation' }
        : { 'aria-label': `${name} mascot`, role: 'img' })}
      className={cn(
        'inline-flex items-center justify-center select-none overflow-hidden',
        shapeClass[shape],
        className,
      )}
      style={{ width: size, height: size, ...style }}
    >
      <img
        src={nameSrc[name]}
        alt=""
        aria-hidden
        draggable={false}
        className="w-full h-full object-contain"
      />
    </div>
  )
}
